import React, { useState } from 'react';
import PlanningTopbar from './PlanningTopbar';
import PlanningPreparer from './PlanningPreparer';
import { ETAPES, premiereAFaire } from './PlanningEtapes';
import { construireColonnes } from '../services/planningPeriodes';
import { exportAPI } from '../services/api';
import { useDemandeEcran } from './aide/AideContexte';
import './PlanningApp.css';

/**
 * Accueil du Planning annuel — lot PA.1 (maquette « accueil 3 » du 28/10), complété
 * au lot PA.2 (maquette « préparer l'année », v94).
 *
 * Le module s'ouvre ici, comme le Stage et le Planning oraux. L'année est celle de
 * la barre de gauche (choix GLOBAL : il n'y en a plus dans le module).
 *
 * Ligne 1 — la vignette « Planning annuel » et les LISTES D'ÉVÉNEMENTS cochées « sur
 * l'accueil ». La vignette Planning annuel a trois visages :
 *   · construit (les 4 étapes faites) → s'ouvre sur le Calendrier ;
 *   · en préparation → orange, dit l'étape à faire, s'ouvre dessus ;
 *   · année VIDE (rien de propre à l'année) → « à préparer » : « Préparer à partir
 *     de <année précédente> » (assistant `PlanningPreparer`) et « Partir de zéro ».
 * Ligne 2 — les CALENDRIERS DES FORMATIONS : les modèles de l'export calendrier
 * cochés « sur l'accueil ». Une vignette de modèle s'ouvre sur l'export, ses
 * réglages chargés ; « Télécharger le PDF » fait la même chose et télécharge : le
 * PDF est fabriqué par l'écran d'export, avec le planning d'aujourd'hui. L'envoi
 * par courriel arrive au lot PA.3.
 */

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août',
    'septembre', 'octobre', 'novembre', 'décembre'];

/** « 2026-09-01 » → « septembre 2026 » (lu comme une date du calendrier, sans fuseau). */
const moisAnnee = (iso) => {
    const [a, m] = String(iso || '').split('-').map(Number);
    return a && m ? `${MOIS[m - 1]} ${a}` : '';
};

/** « 2026-08-09T07:30:34Z » → « 09/08/2026 à 09:30 » (heure de Paris, celle de l'écran). */
const quand = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR') + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

function Chip({ cls = '', ic, children }) {
    return <span className={'pl-acc-chip ' + cls}>{ic && <i className={'fa-solid ' + ic} />} {children}</span>;
}

function Mini({ n, lib, cls = '' }) {
    return <div className={'pl-acc-mini ' + cls}><b>{n}</b><span>{lib}</span></div>;
}

function VignettePlanning({ etat, anneeLibelle, onOuvrir }) {
    const c = etat.chiffres;
    const cle = premiereAFaire(etat);
    const faites = [1, 2, 3, 4].filter((n) => etat.etapes[n]?.faite).length;
    const construit = faites === 4;
    const etape = ETAPES.find((e) => e.cle === cle);
    const periode = etat.premiereDate
        ? `${moisAnnee(etat.premiereDate)} → ${moisAnnee(etat.derniereDate)}`
        : 'aucune période posée';
    const ouvrir = (e) => { if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(); } };

    return (
        <div
            className={'pl-acc-vg' + (construit ? '' : ' prepa')}
            role="button"
            tabIndex={0}
            onClick={ouvrir}
            onKeyDown={ouvrir}
        >
            <div className="pl-acc-vg-h">
                <div className="pl-acc-vg-ic"><i className="fa-solid fa-calendar-days" /></div>
                <div className="pl-acc-vg-tx">
                    <div className="pl-acc-vg-t">Planning annuel {anneeLibelle}</div>
                    <div className="pl-acc-vg-sous">
                        {construit ? `${periode} · ${pluriel(c.groupes, 'groupe', 'groupes')}` : 'en préparation'}
                    </div>
                </div>
            </div>
            <div className="pl-acc-chiffres">
                {construit ? (
                    <>
                        <Mini n={c.groupes} lib="groupes" />
                        <Mini n={c.periodes} lib="périodes" cls="v" />
                        <Mini n={c.evenements} lib="événements" />
                        <Mini n={c.versions} lib="versions" />
                    </>
                ) : (
                    <>
                        <Mini n={`${faites}/4`} lib="étapes" cls="v" />
                        <Mini n={c.groupes} lib="groupes" cls={c.groupes ? '' : 'or'} />
                        <Mini n={c.evenements} lib="événements" cls={c.evenements ? '' : 'or'} />
                        <Mini n={c.periodes} lib="périodes" cls={c.periodes ? '' : 'or'} />
                    </>
                )}
            </div>
            <div className="pl-acc-pied">
                {construit
                    ? <Chip cls="ok" ic="fa-check">4/4 étapes</Chip>
                    : <Chip cls="afaire" ic={etape?.ic}>Étape {etape?.n} à faire : {etape?.lib.toLowerCase()}</Chip>}
                {etat.aVerifier && (
                    <Chip cls="afaire" ic="fa-triangle-exclamation">
                        {pluriel(etat.aVerifier.liste.length + etat.aVerifier.nonReprises.length, 'date à vérifier', 'dates à vérifier')}
                    </Chip>
                )}
                {etat.derniereVersion ? (
                    <span>sauvegardé le {quand(etat.derniereVersion.created_at)} · {etat.derniereVersion.auteur}</span>
                ) : etat.preparation && (
                    <span>préparée à partir de {etat.preparation.source} · {etat.preparation.auteur}</span>
                )}
                <span className="pl-acc-ouvre">
                    {construit ? 's\'ouvre sur le calendrier' : `s'ouvre sur l'étape ${etape?.n}`}
                </span>
            </div>
        </div>
    );
}

function VignetteAPreparer({ anneeLibelle, preparerDepuis, onPreparer, onPartirDeZero, peutEcrire }) {
    return (
        <div className="pl-acc-vg vide">
            <div className="pl-acc-vg-h">
                <div className="pl-acc-vg-ic"><i className="fa-solid fa-calendar-plus" /></div>
                <div className="pl-acc-vg-tx">
                    <div className="pl-acc-vg-t">Planning annuel {anneeLibelle}</div>
                    <div className="pl-acc-vg-sous">à préparer : rien n'est encore saisi pour cette année</div>
                </div>
            </div>
            <div className="pl-acc-gestes">
                {preparerDepuis && (
                    <button type="button" className="pl-btn pl-btn-primary" onClick={onPreparer} disabled={!peutEcrire}
                        title={peutEcrire ? undefined : 'Il faut le droit « modifier » sur le Planning annuel'}>
                        <i className="fa-solid fa-copy" /> Préparer à partir de {preparerDepuis.libelle}
                    </button>
                )}
                <button type="button" className={'pl-btn' + (preparerDepuis ? '' : ' pl-btn-primary')} onClick={onPartirDeZero}>
                    <i className="fa-solid fa-file" /> Partir de zéro
                </button>
            </div>
            <div className="pl-acc-pied">
                <span>les types de journées, d'événements et les formations du département se reprennent tels quels</span>
            </div>
        </div>
    );
}

/** Une vignette de MODÈLE d'export (v94) : le titre, ce qu'il couvre, Télécharger. */
function VignetteModele({ m, colonnes, peutEcrire, onOuvrir, onTelecharger, onEnvoyer, onRetirer }) {
    const evt = m.type === 'evenements';
    const eteintes = new Set(m.config?.colonnesEteintes || []);
    const retenues = colonnes.filter((c) => !eteintes.has(c.cle));
    const ouvrir = (e) => { if (e.type === 'click' || e.key === 'Enter') onOuvrir(); };
    return (
        <div className={'pl-acc-vg' + (evt ? ' evt' : '')} role="button" tabIndex={0} onClick={ouvrir} onKeyDown={ouvrir}>
            <div className="pl-acc-vg-act">
                <button type="button" disabled={!peutEcrire}
                    title={peutEcrire ? 'Retirer de l\'accueil (le modèle reste dans l\'export)' : 'Il faut le droit « modifier » sur le Planning annuel'}
                    onClick={(e) => { e.stopPropagation(); onRetirer(); }}>
                    <i className="fa-solid fa-eye-slash" />
                </button>
            </div>
            <div className="pl-acc-vg-h">
                <div className="pl-acc-vg-ic"><i className={'fa-solid ' + (evt ? 'fa-file-lines' : 'fa-file-pdf')} /></div>
                <div className="pl-acc-vg-tx">
                    <div className="pl-acc-vg-t">{m.titre}</div>
                    <div className="pl-acc-vg-sous">
                        {evt ? 'liste des événements de l\'année'
                            : (retenues.length === colonnes.length ? 'toutes les formations' : retenues.map((c) => c.label).join(' · ') || 'aucune colonne')}
                    </div>
                    {!evt && retenues.length > 0 && retenues.length < colonnes.length && (
                        <div className="pl-acc-formes">
                            {[...new Set(retenues.map((c) => c.niveauCode))].map((n) => <Chip key={n} cls="bleu">{n}</Chip>)}
                        </div>
                    )}
                </div>
            </div>
            <div className="pl-acc-gestes ligne">
                <button type="button" className="pl-btn pl-btn-primary" onClick={(e) => { e.stopPropagation(); onTelecharger(); }}>
                    <i className="fa-solid fa-download" /> Télécharger le PDF
                </button>
                {/* Lot PA.3 : la fenêtre d'envoi s'ouvre sur l'export, ce modèle chargé */}
                <button type="button" className="pl-btn" onClick={(e) => { e.stopPropagation(); onEnvoyer(); }} disabled={!peutEcrire}
                    title={peutEcrire ? (evt ? 'Envoyer aux enseignants du département' : 'Envoyer aux étudiants, apprentis et leurs maîtres') : 'Il faut le droit « modifier » sur le Planning annuel'}>
                    <i className="fa-solid fa-paper-plane" /> Envoyer…
                </button>
            </div>
            <div className="pl-acc-pied">
                {m.obsolete
                    ? <Chip cls="afaire" ic="fa-triangle-exclamation">{evt ? 'événements modifiés' : 'planning modifié'} depuis l'enregistrement</Chip>
                    : <Chip>enregistré le {quand(m.updated_at)}{m.auteur ? ' · ' + m.auteur : ''}</Chip>}
                <span className="pl-acc-ouvre">s'ouvre sur ses réglages</span>
            </div>
        </div>
    );
}

function PlanningAccueil({
    config, departementId, anneeId, peutEcrire,
    etat, erreurEtat, anneeLibelle, sousTitre, onChangeOnglet, onOuvrirPlanning, onOuvrirModele, onRafraichir,
}) {
    const [preparer, setPreparer] = useState(false);
    // Lot PA.4 : « Aller à » depuis l'aide ouvre la fenêtre (l'aide a vérifié avant que l'année est vide)
    useDemandeEcran('accueil', (seg) => { if (seg === 'preparer') setPreparer(true); });
    const [erreur, setErreur] = useState('');
    const colonnes = construireColonnes(config);
    const vignettes = etat?.vignettes || [];
    const listes = vignettes.filter((m) => m.type === 'evenements');
    const calendriers = vignettes.filter((m) => m.type === 'calendrier');

    const retirer = async (m) => {
        setErreur('');
        try {
            await exportAPI.basculerAccueil(m.id, departementId, false);
            onRafraichir();
        } catch (err) {
            setErreur('« ' + m.titre + ' » n\'a pas pu être retiré de l\'accueil : ' + (err?.response?.data?.error || err.message));
        }
    };
    const vignetteModele = (m) => (
        <VignetteModele key={m.id} m={m} colonnes={colonnes} peutEcrire={peutEcrire}
            onOuvrir={() => onOuvrirModele(m)} onTelecharger={() => onOuvrirModele(m, 'telecharger')}
            onEnvoyer={() => onOuvrirModele(m, 'envoyer')} onRetirer={() => retirer(m)} />
    );
    const nouvelle = (onglet) => peutEcrire && etat && !etat.vide && (
        <div className="pl-acc-ligne-btns">
            <button type="button" className="pl-btn" onClick={() => onChangeOnglet(onglet)}
                title="Réglez l'export, puis « Enregistrer le modèle » avec « sur l'accueil » coché">
                <i className="fa-solid fa-plus" /> {onglet === 'exportevt' ? 'Nouvelle liste d\'événements' : 'Nouvelle vignette'}
            </button>
        </div>
    );

    return (
        <div className="pl-root">
            <div className="sticky-top" style={{ margin: 0, top: 0 }}>
                <PlanningTopbar sousTitre={sousTitre} onglet="accueil" onChangeOnglet={onChangeOnglet} />
            </div>

            <div className="pl-content">
                <div className="pl-toolbar">
                    <h2>Planning annuel — {anneeLibelle}</h2>
                    <span className="pl-hint">l'année se choisit dans la barre de gauche</span>
                </div>

                {erreurEtat && (
                    <div className="pl-bandeau pl-bandeau-warn">
                        <i className="fa-solid fa-triangle-exclamation" /> L'état du planning n'a pas pu être lu : {erreurEtat}
                    </div>
                )}
                {erreur && <div className="pl-bandeau pl-bandeau-warn"><i className="fa-solid fa-triangle-exclamation" /> {erreur}</div>}

                {/* Ligne 1 — le planning de l'année et ses listes d'événements (maquette « accueil 3 ») */}
                <div className="pl-acc-ligne">
                    <div className="pl-acc-ligne-h">
                        <span className="pl-acc-ligne-titre">
                            <i className="fa-solid fa-calendar-days" /> Le planning de l'année et ses listes d'événements
                        </span>
                        {nouvelle('exportevt')}
                    </div>
                    <div className="pl-acc-vignettes">
                        {!etat && !erreurEtat && (
                            <div className="pl-acc-vide"><i className="fa-solid fa-spinner fa-spin" /> Lecture de l'état du planning…</div>
                        )}
                        {etat && (etat.vide
                            ? <VignetteAPreparer anneeLibelle={anneeLibelle} preparerDepuis={etat.preparerDepuis} peutEcrire={peutEcrire}
                                onPreparer={() => setPreparer(true)} onPartirDeZero={onOuvrirPlanning} />
                            : <VignettePlanning etat={etat} anneeLibelle={anneeLibelle} onOuvrir={onOuvrirPlanning} />)}
                        {listes.map(vignetteModele)}
                    </div>
                </div>

                {/* Ligne 2 — les calendriers des formations : modèles de l'export calendrier « sur l'accueil » */}
                {etat && (
                    <div className="pl-acc-ligne">
                        <div className="pl-acc-ligne-h">
                            <span className="pl-acc-ligne-titre">
                                <i className="fa-solid fa-file-pdf" /> Calendriers des formations
                            </span>
                            <span className="pl-acc-ligne-n">{calendriers.length}</span>
                            <span className="pl-hint">imprimer le planning d'une formation en un geste</span>
                            {nouvelle('export')}
                        </div>
                        <div className="pl-acc-vignettes">
                            {calendriers.map(vignetteModele)}
                            {!calendriers.length && (
                                <div className="pl-acc-vide">
                                    {etat.vide
                                        ? (etat.preparerDepuis
                                            ? `Pas encore de vignette pour ${anneeLibelle} : « Préparer à partir de ${etat.preparerDepuis.libelle} » reprend celles de l'année d'avant.`
                                            : `Pas encore de vignette pour ${anneeLibelle}.`)
                                        : 'Aucune vignette : dans l\'Export PDF Calendrier, enregistrez un modèle avec « sur l\'accueil » coché.'}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {preparer && (
                <PlanningPreparer
                    departementId={departementId}
                    anneeId={anneeId}
                    onFermer={() => setPreparer(false)}
                    onPrepare={() => { setPreparer(false); onRafraichir(); onChangeOnglet('calendrier'); }}
                />
            )}
        </div>
    );
}

export default PlanningAccueil;
